/*
 * @format
 */

import React from 'react';
import { Circle, ClipPath, Defs, G, Path, Text } from 'react-native-svg';

import { EXAMPLE_ZONE, FACE_RADIUS, RING_RADIUS } from './WatchFace';
import {
  ARC_START_DEG,
  ARC_SWEEP_DEG,
  Crown,
  crownBoxFor,
  dialGeometry,
  dp,
  LABEL_SIZE,
  RING_STROKE,
  StepButton,
  STEP_BUTTON_RADIUS,
  stepButtonCentresFor,
  TurnArrows,
  VALUE_SIZE,
} from './WatchEditorParts';
import {
  DiagramFrame,
  HighlightBand,
  Label,
  Leader,
  MEDIUM,
  stopShortOf,
  TapDot,
  TAP_RADIUS,
} from './DiagramParts';
import { accent, black, primary } from '../../colours';

/*
 * The temperature editor, ported from SetpointEditor.kt: the dial ring behind
 * with the room marker and the draft target on it, a ＋ above and a － below, and
 * the zone name and draft in the middle — the middle being the doorway that
 * saves.
 *
 * The watch draws no "tap to set" caption here; the tap is conveyed by the
 * click role instead. So it is the annotation that says so, not the face.
 *
 * Annotated with the two ways to change it: the buttons, and the crown or bezel.
 */
const TEMP_MIN_C = 5;
const TEMP_MAX_C = 30;

/*
 * The same zone the step before this one draws — the reader is looking at the
 * zone they just tapped.
 *
 * The draft is that zone's own target taken up seven half-degree steps, not a
 * number of its own: SetpointEditor seeds the draft from the existing target
 * (start = zone.targetTempC), so the editor OPENS on the 19.5° the reader just
 * pressed, and 23° is where pressing ＋ has since taken it. Derived from the
 * target rather than restated, so the two steps cannot drift apart into what
 * look like two different zones.
 *
 * Above the room temperature, so the ring is drawn in the state the copy is
 * describing — dialling up — rather than down.
 */
const { name: ZONE_NAME, roomC: ROOM_C } = EXAMPLE_ZONE;
const STEP_C = 0.5; // The half-degree the watch steps in.
const DRAFT_STEPS = 7;
const DRAFT_C = EXAMPLE_ZONE.targetC + DRAFT_STEPS * STEP_C;

const WIDTH = 360;
const HEIGHT = 430;
const CX = WIDTH / 2;
const CY = 176;

const ROOM_DOT = dp(6);
const ROOM_DOT_OUTLINE = dp(2);
const TARGET_DOT = dp(5);
const TRAIL_DOT = dp(1.5);
const TRAIL_ALPHA = 0.8;

/*
 * The centre column is the name over the draft, with no third line beneath —
 * two items, so the pair is set to straddle the face's middle rather than
 * sitting where the three-line duration editor puts its own.
 */
const LABEL_DY = -dp(16);
const VALUE_DY = dp(22);

const TRACK_GREY = primary(900);
const DIM_GREY = primary(300);
const ACCENT_GREEN = accent(400);
const NEUTRAL_WHITE = primary(50);

const { pointAt, arcPath } = dialGeometry(CX, CY);

const fractionFor = celsius =>
  Math.min(Math.max((celsius - TEMP_MIN_C) / (TEMP_MAX_C - TEMP_MIN_C), 0), 1);
const degreesFor = celsius =>
  ARC_START_DEG + fractionFor(celsius) * ARC_SWEEP_DEG;

// The arc stops at whichever of the room and the draft is lower, as the dial's
// own does — here the room, the draft having been taken above it.
const ARC_END_C = Math.min(DRAFT_C, ROOM_C);
const roomMarker = pointAt(RING_RADIUS, degreesFor(ROOM_C));
const targetMarker = pointAt(RING_RADIUS, degreesFor(DRAFT_C));

/*
 * The rising trail: because the draft has been taken ABOVE the room, the watch
 * fills the gap between the arc's end and the target with a dot per whole degree
 * (dialArc/trailBetween in ZoneDial.kt). Only the degrees STRICTLY between the
 * two — the ends already carry the room and target markers — so this example's
 * 21.5 → 23 draws the single dot at 22°.
 *
 * Drawn between the arc and the markers, in the arc's own colour dimmed, as
 * DialRing paints it. Without it the picture shows a ring the watch never
 * renders in the state the copy is describing.
 */
const trailDegrees = [];
for (let degree = Math.floor(ROOM_C) + 1; degree < DRAFT_C; degree += 1) {
  trailDegrees.push(degree);
}

const { plus: plusCentre, minus: minusCentre } = stepButtonCentresFor(CX, CY);

/*
 * The controls caption's two leaders. Each aims at an edge rather than a middle
 * — the dot would otherwise sit on the － itself — and then stops short of it, so
 * the line points at the control instead of touching it.
 */
const CONTROLS_LEADER_FROM = { x: 250, y: 372 };
const LEADER_CLEARANCE = 14;

const MINUS_ANCHOR = stopShortOf(
  CONTROLS_LEADER_FROM,
  { x: minusCentre.x, y: minusCentre.y + STEP_BUTTON_RADIUS },
  LEADER_CLEARANCE,
);

const CROWN = crownBoxFor(CX, CY);
/*
 * The crown's OUTER bottom corner. The inner one sits inside the circle the turn
 * arrows run on, so a leader reaching it has to cut across the lower arrow to get
 * there; the outer corner is beyond that circle, so the whole line stays clear of
 * both arrows.
 */
const CROWN_ANCHOR = stopShortOf(
  CONTROLS_LEADER_FROM,
  { x: CROWN.x + CROWN.width, y: CROWN.y + CROWN.height },
  LEADER_CLEARANCE,
);

/*
 * The band covers the whole centre column, name included: the editor hangs its
 * click on the column rather than on the value alone, so the target really is
 * both lines — unlike the zone screen, where the numeral by itself is the
 * doorway. Padded by the 6dp the column's own clickable carries.
 *
 * A text's y is its baseline with the glyphs standing above it, so the column
 * runs from a cap height above the name's baseline down to the value's.
 */
const LABEL_CAP_HEIGHT = LABEL_SIZE * 0.7;
const COLUMN_PADDING = dp(6);
const COLUMN_TOP = CY + LABEL_DY - LABEL_CAP_HEIGHT;
const COLUMN_BOTTOM = CY + VALUE_DY;
const BAND = {
  x: CX - FACE_RADIUS,
  y: COLUMN_TOP - COLUMN_PADDING,
  width: FACE_RADIUS * 2,
  height: COLUMN_BOTTOM - COLUMN_TOP + COLUMN_PADDING * 2,
};

/*
 * On the band's own centre line, clear of the numeral so it stays readable. Set
 * out further than the duration editor's fingertip: "23.0°" is a wider string
 * than a duration, and at that diagram's offset the disc's shadow would land on
 * the first digit.
 */
const SAVE_TAP = { x: CX - dp(78), y: BAND.y + BAND.height / 2 };

/*
 * The save caption's leader, drawn like the controls caption's pair. It stops
 * short of the fingertip rather than touching it, but by less than the controls'
 * clearance: a full 14 would leave the dot sitting on the dial arc, where it
 * would read as naming the arc instead of the tap. This lands it inside the arc,
 * on the band the fingertip is pressing.
 */
const SAVE_LEADER_FROM = { x: 40, y: 78 };
const SAVE_LEADER_CLEARANCE = 4;
const SAVE_ANCHOR = stopShortOf(
  SAVE_LEADER_FROM,
  SAVE_TAP,
  TAP_RADIUS + SAVE_LEADER_CLEARANCE,
);

function WatchSetpointDiagram() {
  return (
    <DiagramFrame
      width={WIDTH}
      height={HEIGHT}
      description={
        'The watch temperature editor, showing a target of 23 degrees with a ' +
        'plus button above it and a minus button below, and the crown ' +
        'alongside. A line points to a fingertip on the temperature, which is ' +
        'what saves it.'
      }>
      <Defs>
        <ClipPath id="setpoint-face">
          <Circle cx={CX} cy={CY} r={FACE_RADIUS} />
        </ClipPath>
      </Defs>

      <Circle cx={CX} cy={CY} r={FACE_RADIUS} fill={black} />

      {/* The dial's own ring: the unfilled track, the reached portion of it,
          then the room marker with the draft target on top. */}
      <Path
        d={arcPath(RING_RADIUS, ARC_START_DEG, ARC_START_DEG + ARC_SWEEP_DEG)}
        stroke={TRACK_GREY}
        strokeWidth={RING_STROKE}
        strokeLinecap="round"
        fill="none"
      />
      <Path
        d={arcPath(RING_RADIUS, ARC_START_DEG, degreesFor(ARC_END_C))}
        stroke={ACCENT_GREEN}
        strokeWidth={RING_STROKE}
        strokeLinecap="round"
        fill="none"
      />
      {trailDegrees.map(degree => {
        const dot = pointAt(RING_RADIUS, degreesFor(degree));

        return (
          <Circle
            key={degree}
            cx={dot.x}
            cy={dot.y}
            r={TRAIL_DOT}
            fill={ACCENT_GREEN}
            fillOpacity={TRAIL_ALPHA}
          />
        );
      })}
      <Circle
        cx={roomMarker.x}
        cy={roomMarker.y}
        r={ROOM_DOT}
        fill={NEUTRAL_WHITE}
        stroke={black}
        strokeWidth={ROOM_DOT_OUTLINE}
      />
      <Circle
        cx={targetMarker.x}
        cy={targetMarker.y}
        r={TARGET_DOT}
        fill={ACCENT_GREEN}
      />

      <StepButton centre={plusCentre} glyph="+" />
      <StepButton centre={minusCentre} glyph="−" />

      <Text
        x={CX}
        y={CY + LABEL_DY}
        fill={DIM_GREY}
        fontFamily={MEDIUM}
        fontSize={LABEL_SIZE}
        textAnchor="middle">
        {ZONE_NAME}
      </Text>
      {/* The draft takes the arc's own colour, as the editor's does. */}
      <Text
        x={CX}
        y={CY + VALUE_DY}
        fill={ACCENT_GREEN}
        fontFamily={MEDIUM}
        fontSize={VALUE_SIZE}
        textAnchor="middle">
        {`${DRAFT_C.toFixed(1)}°`}
      </Text>

      <G clipPath="url(#setpoint-face)">
        <HighlightBand {...BAND} />
      </G>
      <TapDot x={SAVE_TAP.x} y={SAVE_TAP.y} />

      <TurnArrows cx={CX} cy={CY} />
      <Crown cx={CX} cy={CY} />

      <Leader from={SAVE_LEADER_FROM} to={SAVE_ANCHOR} />
      <Label x={6} y={40} bold heading={['Tap here to', 'save']} />

      <Leader from={CONTROLS_LEADER_FROM} to={MINUS_ANCHOR} />
      <Leader from={CONTROLS_LEADER_FROM} to={CROWN_ANCHOR} />
      <Label
        x={WIDTH - 6}
        y={396}
        anchor="end"
        bold
        heading={['+/- to change or rotate', 'crown / bezel']}
      />
    </DiagramFrame>
  );
}

export default WatchSetpointDiagram;
